import {
	AfterViewInit,
	Component,
	ElementRef,
	Input,
	OnInit,
	ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { openClose } from '@core/animations/animations';
import {
	getMailingBranch,
	getMailingBranchServiceType,
	getServiceBranch,
	getServiceBranchServiceType,
} from '@core/util/llc';
import {
	buildDetailsInformation,
	getInterestRateDescription,
	getMortgageLenderName,
	getMortgagePrimaryLender,
	getMortgageTypePriority,
} from '@core/util/mortgage';
import { Branch, LLCTransaction } from '@models/llc-mortgages';
import {
	BusinessMatterClientWithAssociations,
	PersonMatterClientWithAssociations,
} from '@models/matter';
import {
	MatterMortgagePriority,
	MatterMortgageWithAssociations,
	TransactionType,
} from '@models/mortgage';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { LlcRepoService } from '@repos/mortgages/llc.service';
import { MatterClients } from '@state/global/matter_clients';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterMortgageDispatch } from '@state/matter/matter_mortgages';
import { LLCMortgageDispatch } from '@state/mortgages/llc-mortgage/llc-mortgage.dispatch';
import { LLCMortgageState } from '@state/mortgages/llc-mortgage/llc-mortgage.state';
import { cloneDeep, isEqual } from 'lodash';
import { Observable, Subject } from 'rxjs';
import { map, take } from 'rxjs/operators';
import {
	PortalIframeComponent,
	PortalIFrameData,
} from 'src/app/private/sale/mortgages/llc/components/portal-iframe/portal-iframe.component';
import { ButtonGroupOption } from 'src/app/shared/atomic/atoms/button-group/button-group.component';
import { ICON } from '@core/util/icon';
import { NavigationService } from 'src/app/shared/services/nav/nav.service';
import { ChangePriorityComponent } from '../manual/components/change-priority/change-priority.component';
import { LLCAmendmentsModalComponent } from './components/amendments/amendments.component';
import { AmendmentModalData } from './components/amendments/ammendments.models';
import { StepCalculationsBuilder } from './components/llc-stepper/builders/step-calculations.service';
import {
	LLCExistingMatterModalComponent,
	LLCExistingMatterModalData,
} from './sync/existing-matter/existing-matter.component';
import {
	LLCNewMatterModalComponent,
	LLCNewMatterModalData,
} from './sync/new-matter/new-matter.component';
import { LenderWithAssociations } from '@models/lender';
import { MortgageDetailsOverview } from '../mortgages.models';
import { MortgageTransactionStatus } from '@models/mortgages/mortgage-base';

enum DetailNavType {
	MORTGAGE_DETAILS = 'mortgageDetails',
	LENDER_DETAILS = 'lenderDetails',
	PAYMENT = 'payment',
	REPORT = 'report',
	DOCUMENTS = 'documents',
}

const DetailsNavOptions: ButtonGroupOption<DetailNavType>[] = [
	{ label: 'General Details', value: DetailNavType.MORTGAGE_DETAILS },
	// { label: 'Lender details', value: DetailNavType.LENDER_DETAILS },
	{ label: 'Terms & Payment', value: DetailNavType.PAYMENT },
	{ label: 'Registration Details', value: DetailNavType.REPORT },
];

@UntilDestroy()
@Component({
	selector: 'app-matter-llc',
	templateUrl: './llc.component.html',
	styleUrls: ['./llc.component.scss'],
	animations: [openClose()],
	standalone: false,
})
export class LlcMatterMortgageComponent implements OnInit, AfterViewInit {
	public _matterMortgage: MatterMortgageWithAssociations;
	public _mortgage: LLCTransaction;
	public transactionTypes = TransactionType;
	public ICON = ICON;
	public variable = false;

	public detailsNavOptions = DetailsNavOptions;
	public selectedDetailNav = DetailNavType.MORTGAGE_DETAILS;
	public detailNavTypes = DetailNavType;
	public mortgageStatuses = MortgageTransactionStatus;
	public mortgagePriorities = MatterMortgagePriority;
	public serviceBranch: Branch;
	public serviceBranchCentreType;
	public mailingBranch: Branch;
	public mailingBranchCentreType;
	public details: MortgageDetailsOverview;

	public lenderName: string;
	public priority: string;
	public interestRate: string;
	public principal: number;
	public showRefresh = false;
	public clientsMapped = false;
	private clientsAssociationsMapped: (
		| PersonMatterClientWithAssociations
		| BusinessMatterClientWithAssociations
	)[] = [];
	private guarantorsAssociationsMapped: (
		| PersonMatterClientWithAssociations
		| BusinessMatterClientWithAssociations
	)[] = [];

	private detectReconcileBorrowers = new Subject<void>();

	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@Select(LLCMortgageState.getLLCMortgage) llcMortgageFilterFn$: Observable<
		(id) => LLCTransaction
	>;

	@Select(MatterClients.get_matterClientsWithAssociationMapped)
	clientsAssociationsMapped$: Observable<
		(
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[]
	>;
	@Select(MatterClients.get_guarantorsWithAssociationMapped)
	guarantorsAssociationsMapped$: Observable<
		(
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[]
	>;

	@Input() set matterMortgage(mortgage: MatterMortgageWithAssociations) {
		this._matterMortgage = mortgage;

		this.getMortgage$(mortgage.relatedMortgageId).subscribe((transaction) => {
			this._mortgage = transaction;

			if (transaction) {
				this.variable =
					transaction.data?.mortgage?.interestRate?.interestRateType?.toLowerCase() ===
					'variable';
				// if (transaction.status === MortgageTransactionStatus.COMPLETED) {
				this.detailsNavOptions = [
					...DetailsNavOptions,
					{ label: 'Documents', value: DetailNavType.DOCUMENTS },
				];
				// }
				this.serviceBranch = getServiceBranch(transaction?.data);
				this.serviceBranchCentreType = getServiceBranchServiceType(
					transaction?.data,
				);
				this.mailingBranch = getMailingBranch(transaction?.data);
				this.mailingBranchCentreType = getMailingBranchServiceType(
					transaction?.data,
				);

				if (mortgage) {
					const lender = getMortgagePrimaryLender(
						mortgage,
					) as LenderWithAssociations;

					this.principal = mortgage?.amount ?? 0;
					this.priority = getMortgageTypePriority(mortgage);
					this.lenderName = lender ? getMortgageLenderName(lender) : '';
					this.interestRate = getInterestRateDescription(
						mortgage?.interestRate,
						mortgage?.interestRateType,
						mortgage?.interestAbovePrime,
					);
				}

				// below is done to figure out if the mapped code has changed and this mortgage needs to be updated
				// this is a catch all if there is a bug in the system after a user has mapped an llc mortgage

				const mappedMortage = this.llcService.mapLLCToCloserMortgageData(
					mortgage?.relatedMortgageId,
					transaction,
					this._matterMortgage.transactionType,
				);
				const clonedMortgage = {
					...cloneDeep(this._matterMortgage),
					...mappedMortage,
				};

				this.showRefresh = !isEqual(clonedMortgage, this._matterMortgage);

				this.detectReconcileBorrowers.next();
			}
		});

		this.details = buildDetailsInformation(mortgage);

		this.llcDispatch.UpdateTransaction(mortgage?.relatedMortgageId);
	}

	@ViewChild('buttonGroup', { read: ElementRef, static: true }) buttonGroup;

	constructor(
		private route: ActivatedRoute,
		private dialog: MatDialog,
		private navService: NavigationService,
		private llcDispatch: LLCMortgageDispatch,
		private llcService: LlcRepoService,
		private mortgageDispatch: MatterMortgageDispatch,
		private stepCalculator: StepCalculationsBuilder,
		private matDialog: MatDialog,
	) {}

	ngOnInit(): void {
		this.detectReconcileBorrowers.pipe(untilDestroyed(this)).subscribe(() => {
			this.calculateReconcileBorrowers(
				this._mortgage,
				this.matterId,
				this._matterMortgage.id,
				this.clientsAssociationsMapped,
				this.guarantorsAssociationsMapped,
			);
		});

		this.clientsAssociationsMapped$
			.pipe(untilDestroyed(this))
			.subscribe((clients) => {
				this.clientsAssociationsMapped = clients;

				this.detectReconcileBorrowers.next();
			});

		this.guarantorsAssociationsMapped$
			.pipe(untilDestroyed(this))
			.subscribe((guarantors) => {
				this.guarantorsAssociationsMapped = guarantors;
				this.detectReconcileBorrowers.next();
			});
	}

	ngAfterViewInit(): void {
		this.route.queryParams
			.pipe(untilDestroyed(this))
			.subscribe((queryparams) => {
				const { modal } = queryparams;

				if (modal === 'createSync') {
					const data: LLCNewMatterModalData = {
						transactionId: this._matterMortgage.relatedMortgageId,
					};
					const newMatterDialogRef = this.dialog.open(
						LLCNewMatterModalComponent,
						{ data },
					);
					newMatterDialogRef
						.afterClosed()
						.pipe(take(1))
						.subscribe(() => {
							this.navService.goToMatter_Mortgages_Mortgage(
								this.matterId,
								this._matterMortgage.id,
							);
						});
				} else if (modal === 'assignSync') {
					const data: LLCExistingMatterModalData = {
						transactionId: this._matterMortgage.relatedMortgageId,
						matterMortgageId: this._matterMortgage.id,
					};

					const existingDialogRef = this.dialog.open(
						LLCExistingMatterModalComponent,
						{ data },
					);
					existingDialogRef
						.afterClosed()
						.pipe(take(1))
						.subscribe(() => {
							this.navService.goToMatter_Mortgages_Mortgage(
								this.matterId,
								this._matterMortgage.id,
							);
						});
				} else if (modal === 'llc-portal') {
					const data: PortalIFrameData = {
						transaction: this._mortgage,
						matterMortgage: this._matterMortgage,
						readonlyMode: false,
					};
					this.dialog.open(PortalIframeComponent, { data });
				}
			});
	}

	calculateReconcileBorrowers(
		mortgage: LLCTransaction,
		matterId: string,
		matterMortgageId: string,
		matterClients: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		guarantors: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
	) {
		const borrowersReconciled = this.stepCalculator.getBorrowersReconcileStatus(
			mortgage,
			matterId,
			matterMortgageId,
			matterClients,
		);
		const guarantorsReconciled =
			this.stepCalculator.getGuarantorsReconcileStatus(
				mortgage,
				matterId,
				matterMortgageId,
				guarantors,
			);

		this.clientsMapped =
			borrowersReconciled?.completed &&
			(!guarantorsReconciled || guarantorsReconciled?.completed);
	}

	reconcileBorrowers() {
		this.navService.goToMatter_Mortgages_Mortgage_AssignSync(
			this.matterId,
			this._matterMortgage.id,
		);
	}

	getMortgage$(id: string): Observable<LLCTransaction> {
		return this.llcMortgageFilterFn$.pipe(
			untilDestroyed(this),
			map((fn) => fn(id)),
		);
	}

	getContactInitials = (firstName?: string, lastName?: string): string => {
		let result = '';

		if (firstName?.length > 0) {
			result += firstName[0];
		}

		if (lastName?.length > 0) {
			result += lastName[0];
		}

		return result;
	};

	goToLLC() {
		const data: PortalIFrameData = {
			transaction: this._mortgage,
			matterMortgage: this._matterMortgage,
		};

		this.dialog.open(PortalIframeComponent, { data });
	}

	viewAmendments() {
		const data: AmendmentModalData = {
			transaction: this._mortgage,
		};
		this.dialog.open(LLCAmendmentsModalComponent, { data });
	}

	resyncData() {
		const matterMortgageUpdated = this.llcService.mapLLCToCloserMortgageData(
			this._mortgage.id,
			this._mortgage,
			this._matterMortgage.transactionType,
		);
		this.mortgageDispatch.updateMortgage(
			this._matterMortgage.id,
			matterMortgageUpdated,
		);
	}

	editPriority() {
		this.matDialog.open(ChangePriorityComponent, { panelClass: 'gradient' });
	}

	viewAll() {
		this.selectedDetailNav = DetailNavType.PAYMENT;
		this.buttonGroup?.nativeElement?.scrollIntoView({ behavior: 'smooth' });
	}
}
